import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getIndustries } from "../../Api/Users/Lookups/GetIndustries";
import { getSpecialities } from "../../Api/Users/Lookups/GetSpecialities";
import axiosInstance from "../../axiosInstance";
import Select from "react-select";
import FormError from "../../pages/Auth1/FormError";

const ProfileEdit = () => {
  const { t, i18n } = useTranslation();
  const languageDirection = i18n.language === "ar" ? "rtl" : "ltr";
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isPaid, setIsPaid] = useState(false);
  const [price, setPrice] = useState("");
  const [showPriceInProfile, setShowPriceInProfile] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [priceRanges, setPriceRanges] = useState([]);
  const [experienceTypes, setExperienceTypes] = useState([]);
  const [cvLink, setcvLink] = useState(null);

  // تحويل البيانات إلى التنسيق المطلوب
  const formatOptions = (items, type) =>
    items.map((item) => {
      let data = item;
      if (type === "state" && item.state) {
        data = item.state;
      } else if (type === "city" && item.city) {
        data = item.city;
      }
      return {
        value: data?.id || item.id,
        label:
          i18n.language === "ar"
            ? data?.arabicName || data?.name || item.arabicName || item.name
            : data?.englishName || data?.name || item.englishName || item.name,
      };
    });

  const [formData, setFormData] = useState({
    englishFullName: "",
    arabicFullName: "",
    phoneNumber: "",
    gender: "",
    birthDate: "",
    nationalityId: "",
    countryId: "",
    stateId: "",
    cityId: "",
    profileImage: "",
    profileCoverImage: "",
    price: "",
    isPaid: false,
    showPriceInProfile: false,
    englishOrganizationName: "",
    arabicOrganizationName: "",
    englishJobTitle: "",
    arabicJobTitle: "",
    organizationLink: "",
    arabicShortBiography: "",
    englishShortBiography: "",
    arabicCompleteBiography: "",
    englishCompleteBiography: "",
    industryId: "",
    specialities: [],
    cvLink: "",
    file: null,
    speakerExperiences: [],
    scholarLink: "",
    websiteLink: "",
    linkedInLink: "",
    facebookLink: "",
    xLink: "",
    githubLink: "",
    youtubeLink: "",
    arabicTitle: "",
    englishTitle: "",
    arabicEntity: "",
    englishEntity: "",
    timing: "",
    experienceType: "",
  });

  // 1. دوال API
  const getProfile = async () => {
    try {
      const response = await axiosInstance.get("/api/v1/profile");
      return response.data;
    } catch (error) {
      console.error("Error fetching profile:", error);
      throw error;
    }
  };

  const updateProfile = async (data) => {
    try {
      const config = {};

      if (data instanceof FormData) {
        config.headers = {
          "Content-Type": "multipart/form-data",
        };
      }

      const response = await axiosInstance.put("/api/v1/profile", data, config);

      if (response.status === 200) {
        return { success: true, data: response.data };
      } else {
        return { success: false, errors: response.data.errors };
      }
    } catch (error) {
      const errorData = error?.response?.data;
      const validationErrors = {};

      if (errorData?.errors && typeof errorData.errors === "object") {
        const allMessages = [];

        for (const [field, messages] of Object.entries(errorData.errors)) {
          if (Array.isArray(messages)) {
            validationErrors[field] = messages.join(", ");
            allMessages.push(...messages);
          }
        }

        allMessages.forEach((msg) => toast.error(msg));
      }

      if (!errorData?.errors) {
        const fallbackError = error.detail || error.title || error.message;
        toast.error(fallbackError);
      }

      return { success: false, errors: validationErrors };
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axiosInstance.get("/api/v1/lookups/countries");
      setCountries(response.data.items);
    } catch (error) {
      toast.error("Error fetching countries");
    }
  };

  const fetchStates = async (countryId) => {
    if (!countryId) {
      setStates([]);
      setCities([]);
      return;
    }

    try {
      const response = await axiosInstance.get(
        `/api/v1/lookups/states?countryId=${countryId}`
      );
      setStates(response.data.items);
    } catch (error) {
      setStates([]);
      setCities([]);
      toast.error("Error fetching states");
    }
  };

  const fetchCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }

    try {
      const response = await axiosInstance.get(
        `/api/v1/lookups/cities?stateId=${stateId}`
      );
      setCities(response.data.items);
    } catch (error) {
      setCities([]);
      toast.error("Error fetching cities");
    }
  };

  const fetchIndustries = async () => {
    try {
      const industriesData = await getIndustries();
      setIndustries(industriesData || []);
    } catch (error) {
      toast.error("Error fetching industries.");
    }
  };

  const fetchSpecialities = async () => {
    if (formData.industryId) {
      try {
        const specialitiesData = await getSpecialities(formData.industryId);
        setSpecialities(specialitiesData || []);
      } catch (error) {
        toast.error("Error fetching specialities.");
      }
    } else {
      setSpecialities([]);
    }
  };

  // 2. الدوال الحدثية
  const handleChange = (e) => {
    const { name, value, type, options } = e.target;
    let updatedValue = value;

    if (type === "select-multiple") {
      updatedValue = Array.from(options)
        .filter((option) => option.selected)
        .map((option) => option.value);
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: undefined,
    }));

    setFormData((prevData) => ({
      ...prevData,
      [name]: updatedValue,
    }));
  };

  const [imageFiles, setImageFiles] = useState({
    profileImage: null,
    profileCoverImage: null,
  });

  const handleImageChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const reader = new FileReader();

      reader.onload = () => {
        setFormData((prevData) => ({
          ...prevData,
          [name]: reader.result, // فقط للمعاينة
        }));

        setImageFiles((prev) => ({
          ...prev,
          [name]: file,
        }));
      };

      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    toast.dismiss();
    setErrors({});

    try {
      const data = new FormData();

      // البيانات النصية
      data.append("EnglishFullName", formData.englishFullName);
      data.append("ArabicFullName", formData.arabicFullName);
      data.append("PhoneNumber", formData.phoneNumber);
      data.append("birthDate", formData.birthDate || "");
      data.append("gender", formData.gender || "");
      data.append("nationalityId", formData.nationalityId || "");
      data.append("countryId", formData.countryId || "");
      data.append("stateId", formData.stateId || "");
      data.append("cityId", formData.cityId || "");
      data.append("IsPaid", isPaid ? "true" : "false");

      if (isPaid) {
        data.append("price", price);
      }

      data.append("ShowPriceInProfile", showPriceInProfile ? "true" : "false");

      data.append("EnglishOrganizationName", formData.englishOrganizationName);
      data.append("ArabicOrganizationName", formData.arabicOrganizationName);
      data.append("EnglishJobTitle", formData.englishJobTitle);
      data.append("ArabicJobTitle", formData.arabicJobTitle);
      data.append("OrganizationLink", formData.organizationLink);
      data.append("ScholarLink", formData.scholarLink);
      data.append("WebsiteLink", formData.websiteLink);
      data.append("LinkedInLink", formData.linkedInLink);
      data.append("FacebookLink", formData.facebookLink);
      data.append("XLink", formData.xLink);
      data.append("GithubLink", formData.githubLink);
      data.append("YoutubeLink", formData.youtubeLink);
      data.append("EnglishShortBiography", formData.englishShortBiography);
      data.append("ArabicShortBiography", formData.arabicShortBiography);
      data.append(
        "EnglishCompleteBiography",
        formData.englishCompleteBiography
      );
      data.append("ArabicCompleteBiography", formData.arabicCompleteBiography);
      data.append("IndustryId", formData.industryId);

      // إرسال الصور الفعلية من imageFiles
      if (imageFiles.profileImage) {
        data.append("ProfileImage", imageFiles.profileImage);
      }

      if (imageFiles.profileCoverImage) {
        data.append("ProfileCoverImage", imageFiles.profileCoverImage);
      }

      // ملف السيرة الذاتية (CV)
      if (formData.file) {
        data.append("cvLink", formData.file, formData.file.name);
      }

      // التخصصات
      if (
        Array.isArray(formData.specialities) &&
        formData.specialities.length > 0
      ) {
        const uniqueSpecialities = Array.from(new Set(formData.specialities));
        uniqueSpecialities.forEach((id) => {
          data.append("Specialities", id);
        });
      }

      // الخبرات
      (formData.speakerExperiences || []).forEach((exp, index) => {
        data.append(`UserExperiences[${index}].ArabicTitle`, exp.arabicTitle);
        data.append(`UserExperiences[${index}].EnglishTitle`, exp.englishTitle);
        data.append(`UserExperiences[${index}].ArabicEntity`, exp.arabicEntity);
        data.append(
          `UserExperiences[${index}].EnglishEntity`,
          exp.englishEntity
        );
        data.append(`UserExperiences[${index}].Timing`, exp.timing);
        data.append(
          `UserExperiences[${index}].ExperienceType`,
          exp.experienceType
        );
      });

      console.log("speakerExperiences:", formData.speakerExperiences);

      // إرسال البيانات
      const response = await updateProfile(data);
      console.log("response", response);

      if (response.success) {
        toast.success(t("profile_updated"));

        const updatedProfile = await getProfile();
        console.log("CV LINK:", updatedProfile.cvLink);
        setFormData((prev) => ({
          ...prev,
          ...updatedProfile,
          cvLink: updatedProfile.cvLink || "",
          specialities: updatedProfile.specialities?.map((s) => s.id) || [],
          industryId: updatedProfile.industry?.id || "",
          speakerExperiences: updatedProfile.speakerExperiences || [],
          profileImage: updatedProfile.profileImage || "",
          profileCoverImage: updatedProfile.profileCoverImage || "",
        }));
        setFormData((prev) => ({
          ...prev,
          speakerExperiences: updatedProfile.speakerExperiences || [],
        }));

        setPrice(
          updatedProfile.price === "0-0" ? "" : updatedProfile.price || ""
        );
        setIsPaid(updatedProfile.isPaid || false);
        setShowPriceInProfile(updatedProfile.showPriceInProfile || false);

        setImageFiles({
          profileImage: null,
          profileCoverImage: null,
        });
      } else {
        if (response.errors) {
          setErrors(response.errors);
          for (let field in response.errors) {
            toast.error(response.errors[field].join(", "));
          }
        } else {
          toast.error(t("unexpected_error"));
        }
      }
    } catch (error) {
      if (error.detail || error.title || error.message) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t("unexpected_error"));
      }
    }

    setIsLoading(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        file: file,
      }));
    }
  };

  // 3. الـ useEffect
  useEffect(() => {
    getProfile()
      .then((response) => {
        console.log("PROFILE RESPONSE:", response);

        const city = response?.city;
        const state = city?.state;
        const country = state?.country;

        console.log("City:", city);
        console.log("State:", state);
        console.log("Country:", country);
        console.log("Price:", price);
        console.log("cvLink:", cvLink);
        console.log("speakerExperiences:", response?.speakerExperiences);
        console.log("Full response keys:", Object.keys(response));

        setFormData({
          englishFullName: response?.englishFullName || "",
          arabicFullName: response?.arabicFullName || "",
          phoneNumber: response?.phoneNumber || "",
          gender: response?.gender || "",
          birthDate: response?.birthDate?.split("T")[0] || "",
          nationalityId: response?.nationalityId || "",
          countryId: country?.id || "",
          stateId: state?.id || "",
          cityId: city?.id || "",
          profileImage: response?.profileImage || "",
          profileCoverImage: response?.profileCoverImage || "",
          price: response?.price || "",
          isPaid: response?.isPaid || false,
          showPriceInProfile: response?.showPriceInProfile || false,
          englishOrganizationName: response?.englishOrganizationName || "",
          arabicOrganizationName: response?.arabicOrganizationName || "",
          englishJobTitle: response?.englishJobTitle || "",
          arabicJobTitle: response?.arabicJobTitle || "",
          organizationLink: response?.organizationLink || "",
          arabicShortBiography: response?.arabicShortBiography || "",
          englishShortBiography: response?.englishShortBiography || "",
          arabicCompleteBiography: response?.arabicCompleteBiography || "",
          englishCompleteBiography: response?.englishCompleteBiography || "",
          industryId: response?.industryId || "",
          specialities:
            response?.speakerSpecialies?.map((s) => s.speciality?.id) || [],
          speakerExperiences: response?.speakerExperiences || [],
          cvLink: response?.cvLink || "",
          scholarLink: response?.scholarLink || "",
          linkedInLink: response?.linkedInLink || "",
          xLink: response?.xLink || "",
          websiteLink: response?.websiteLink || "",
          githubLink: response?.githubLink || "",
          facebookLink: response?.facebookLink || "",
          youtubeLink: response?.youtubeLink || "",
        });
        setUserRole(response?.userRole);
      })
      .catch((error) => {
        console.error("Error fetching profile:", error);
        toast.error(t("login.serverError"));
      });
  }, []);

  useEffect(() => {
    fetchCountries();
    fetchIndustries();
  }, []);

  useEffect(() => {
    if (formData.countryId) {
      fetchStates(formData.countryId);
    }
  }, [formData.countryId]);

  useEffect(() => {
    if (formData.stateId) {
      fetchCities(formData.stateId);
    }
  }, [formData.stateId]);

  useEffect(() => {
    fetchSpecialities();
  }, [formData.industryId]);

  const maxLength = 150;
  const maxFullLength = 500;

  const getColorForTextArea = (textLength, max = maxLength) => {
    if (textLength === 0) return "#ced4da";
    const percentage = (textLength / max) * 100;
    if (percentage < 50) return "#28a745"; // أخضر
    if (percentage < 90) return "#ffc107"; // أصفر
    return "#dc3545"; // أحمر
  };

  // تحميل البيانات عند تحميل الصفحة
  // الخبرات
  useEffect(() => {
    const fetchExperienceTypes = async () => {
      try {
        const response = await axiosInstance.get("/api/v1/lookups/experiences");
        setExperienceTypes(response.data.items);
      } catch (error) {
        console.error("فشل في تحميل أنواع الخبرات", error);
      }
    };

    fetchExperienceTypes();
  }, []);

  // جلب بيانات النطاقات السعرية
  useEffect(() => {
    axiosInstance
      .get("/api/v1/lookups/prices-range")
      .then((response) => {
        setPriceRanges(response.data.items); // تخزين نطاقات الأسعار
      })
      .catch((error) => {
        console.error("Error fetching price ranges:", error);
      });

    // جلب بيانات البروفايل باستخدام الدالة getProfile
    getProfile()
      .then((profile) => {
        console.log("Profile data fetched:", profile); // تحقق من البيانات في الكونسول
        setIsPaid(profile.isPaid === true); // تأكد من نوع البيانات
        setPrice(profile.price ?? ""); // التأكد من التعامل مع nullish value
        setShowPriceInProfile(profile.showPriceInProfile ?? false);
        setFormData((prev) => ({
          ...prev,
          ...profile,
          speakerExperiences: profile?.speakerExperiences || [],
        }));
      })
      .catch((error) => {
        console.error("Error fetching profile:", error);
      });
  }, []);

  useEffect(() => {
    // بعد تغيير البيانات
    console.log("Updated state values:", isPaid, price, showPriceInProfile);
  }, [isPaid, price, showPriceInProfile]); // مراقبة التغييرات في هذه القيم

  const addExperience = () => {
    const newExperiences = [
      ...formData.speakerExperiences,
      {
        arabicTitle: "",
        englishTitle: "",
        arabicEntity: "",
        englishEntity: "",
        timing: "",
        experienceType: "",
      },
    ];
    setFormData({ ...formData, speakerExperiences: newExperiences });
  };

  const removeExperience = (index) => {
    const updated = formData.speakerExperiences.filter((_, i) => i !== index);
    setFormData({ ...formData, speakerExperiences: updated });
  };

  const handleChangeExperience = (index, field, value) => {
    const updated = [...formData.speakerExperiences];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setFormData({ ...formData, speakerExperiences: updated });
  };

  return (
    <>
      <style>
        {`
    .account-type-card {
      border: 2px solid #e9ecef;
      border-radius: 8px;
      transition: all 0.3s ease;
      cursor: pointer;
      position: relative;
    }
    
    .account-type-card:hover {
      border-color: var(--primary-color);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    .account-type-card .form-check-input {
      position: absolute;
      top: 10px;
      right: 10px;
      margin: 0;
    }
    
    .account-type-card.selected {
      border-color: var(--primary-color);
      background-color: rgba(var(--primary-color-rgb), 0.05);
    }
    
    .form-check-input:checked + .form-check-label .fa-2x {
      color: var(--primary-color);
    }
    .profile-header {
      position: relative;
      height: 200px;
      background-color: #f0f4f8;
      background-size: cover;
      background-position: center;
      border-radius: 10px 10px 0 0;
      overflow: hidden;
    }
    
    .cover-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.4));
    }
    
    .cover-image-upload {
      position: absolute;
      bottom: 10px;
      right: 10px;
      background-color: rgba(255,255,255,0.8);
      color: var(--bs-primary);
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(0,0,0,0.2);
      transition: all 0.3s ease;
      z-index: 10;
    }
    
    .cover-image-upload:hover {
      background-color: white;
      transform: scale(1.05);
    }
    
    .profile-image-container {
      position: relative;
      margin-top: -75px;
      margin-bottom: 2rem;
      text-align: center;
      z-index: 10;
    }
    
    .profile-image {
      width: 150px;
      height: 150px;
      border-radius: 50%;
      object-fit: cover;
      border: 4px solid #f8f9fa;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      background-color: white;
    }
    
    .profile-image-upload {
      position: absolute;
      bottom: 0;
      right: 50%;
      transform: translateX(50%);
      background-color: var(--bs-primary);
      color: white;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(0,0,0,0.2);
      transition: all 0.3s ease;
    }
    
    .profile-image-upload:hover {
      background-color: var(--bs-primary-hover);
      transform: translateX(50%) scale(1.05);
    }
    
    .form-section {
      margin-bottom: 2.5rem;
    }
    
    .section-header {
      border-bottom: 2px solid #f0f0f0;
      padding-bottom: 0.75rem;
      margin-bottom: 1.5rem;
    }
    

    .form-select, .form-control {
      padding: 0.75rem 1rem;
      border-radius: 8px;
    }
    
    .form-select:focus, .form-control:focus {
      border-color: var(--bs-primary);
      box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25);
    }

    .custom-file-upload {
      display: inline-block;
      padding: 0.5rem 1rem;
      cursor: pointer;
      background-color: #f8f9fa;
      border: 1px solid #ddd;
      border-radius: 4px;
      transition: all 0.3s;
    }
    
    .custom-file-upload:hover {
      background-color: #e9ecef;
    }
    
   
    .specialty-tag {
      display: inline-block;
      background-color: var(--bs-primary-light);
      color: var(--bs-primary);
      border-radius: 20px;
      padding: 0.35rem 0.75rem;
      margin: 0.25rem;
      font-size: 0.875rem;
    }
    
    .specialty-tag .remove-tag {
      margin-right: 0.5rem;
      cursor: pointer;
    }
    

    .tags-container {
      margin-top: 0.75rem;
    }
    

    .account-type-card {
      cursor: pointer;
      transition: all 0.3s ease;
      border: 2px solid transparent;
      height: 100%;
    }
    
    .account-type-card:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0,0,0,0.1);
    }
    
    .account-type-card.border-primary {
      border-color: var(--bs-primary) !important;
      background-color: rgba(var(--bs-primary-rgb), 0.05);
    }
    
    .account-type-card .form-check-input {
      position: absolute;
      top: 10px;
      right: 10px;
    }

    body {
      min-height: 100vh;
    }
        `}
      </style>

      <div dir={languageDirection}>
        <section className="bg-light py-5">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <h1 className="text-center mb-4 fw-bold text-primary-custom">
                  {t("edit_profile")}
                </h1>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb justify-content-center">
                    <li className="breadcrumb-item">
                      <a href="/">{t("home")}</a>
                    </li>
                    {/* <li className="breadcrumb-item">
                      <a href="profile">{t("my_profile")}</a>
                    </li> */}
                    <li className="breadcrumb-item active" aria-current="page">
                      {t("edit_profile")}
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </section>

        {/* Profile Edit Form Section  */}
        <section className="py-5">
          <div className="container profile-edit-container">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="card shadow-sm">
                  <div className="profile-header" id="coverImageContainer">
                    <div className="cover-overlay">
                      <img
                        src={
                          formData.profileCoverImage ||
                          "assets/default-cover.png"
                        }
                        alt="Cover"
                        className="cover-image"
                      />
                    </div>
                    <label
                      htmlFor="profileCoverImage"
                      className="cover-image-upload"
                    >
                      <i className="fas fa-camera"></i>
                    </label>
                    <input
                      type="file"
                      id="profileCoverImage"
                      name="profileCoverImage"
                      className="d-none"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    <img
                      src={
                        formData.profileCoverImage || "assets/default-cover.png"
                      }
                      alt="Cover"
                      className="cover-image"
                    />
                    {errors.profileCoverImage && (
                      <FormError errorMessage={errors.profileCoverImage[0]} />
                    )}
                  </div>

                  <div className="card-body p-4 p-lg-5">
                    <form onSubmit={handleSubmit} noValidate>
                      {/* صورة البروفايل */}
                      <div className="profile-image-container text-center mb-4">
                        <img
                          src={formData.profileImage}
                          alt="Profile"
                          className="profile-image"
                        />
                        <label
                          htmlFor="profileImage"
                          className="profile-image-upload"
                        >
                          <i className="fas fa-camera"></i>
                        </label>
                        <input
                          type="file"
                          id="profileImage"
                          name="profileImage"
                          className="d-none"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                        {errors.profileImage && (
                          <FormError errorMessage={errors.profileImage} />
                        )}
                        <div className="mt-2">
                          <small className="text-muted">
                            {t("update_profile_image")}
                          </small>
                        </div>
                      </div>
                      {/* قسم المعلومات الشخصية */}
                      <div className="form-section">
                        <h3 className="section-header text-primary-custom fw-bold">
                          {t("personal_information")}
                        </h3>
                        <div className="row g-4">
                          <div className="col-md-6">
                            <label
                              htmlFor="arabicFullName"
                              className="form-label"
                            >
                              {t("name_ar")}
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                errors.arabicFullName ? "is-invalid" : ""
                              }`}
                              id="arabicFullName"
                              name="arabicFullName"
                              value={formData.arabicFullName}
                              onChange={handleChange}
                            />
                            {errors.arabicFullName && (
                              <FormError errorMessage={errors.arabicFullName} />
                            )}
                          </div>

                          <div className="col-md-6">
                            <label
                              htmlFor="englishFullName"
                              className="form-label"
                            >
                              {t("name_en")}
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                errors.englishFullName ? "is-invalid" : ""
                              }`}
                              id="englishFullName"
                              name="englishFullName"
                              value={formData.englishFullName}
                              onChange={handleChange}
                            />
                            {errors.englishFullName && (
                              <FormError
                                errorMessage={errors.englishFullName}
                              />
                            )}
                          </div>
                          <div className="col-md-4">
                            <label htmlFor="email" className="form-label">
                              {t("email")}
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              id="email"
                              name="email"
                              value={formData.email}
                              disabled
                            />
                          </div>

                          <div className="col-md-4">
                            <label htmlFor="phoneNumber" className="form-label">
                              {t("mobile_number")}
                            </label>
                            <input
                              type="tel"
                              className={`form-control ${
                                errors.phoneNumber ? "is-invalid" : ""
                              }`}
                              id="phoneNumber"
                              name="phoneNumber"
                              value={formData.phoneNumber}
                              onChange={handleChange}
                              dir="ltr"
                            />
                            {errors.phoneNumber && (
                              <FormError errorMessage={errors.phoneNumber} />
                            )}
                          </div>

                          <div className="col-md-4">
                            <label htmlFor="gender" className="form-label">
                              {t("gender")}
                            </label>
                            <select
                              className={`form-select ${
                                errors.gender ? "is-invalid" : ""
                              }`}
                              id="gender"
                              name="gender"
                              value={formData.gender}
                              onChange={handleChange}
                            >
                              <option value="">{t("select_gender")}</option>
                              <option value="1">{t("male")}</option>
                              <option value="2">{t("female")}</option>
                            </select>

                            {errors.gender && (
                              <FormError errorMessage={errors.gender} />
                            )}
                          </div>

                          <div className="col-md-6">
                            <label htmlFor="birthDate" className="form-label">
                              {t("birthdate")}
                            </label>
                            <input
                              type="date"
                              className={`form-control ${
                                errors.birthDate ? "is-invalid" : ""
                              }`}
                              id="birthDate"
                              name="birthDate"
                              value={formData.birthDate}
                              onChange={handleChange}
                            />
                            {errors.birthDate && (
                              <FormError errorMessage={errors.birthDate} />
                            )}
                          </div>

                          <div className="col-md-6">
                            <label
                              htmlFor="nationalityId"
                              className="form-label"
                            >
                              {t("nationality")}
                            </label>
                            <Select
                              id="nationalityId"
                              name="nationalityId"
                              options={formatOptions(countries)}
                              value={formatOptions(countries).find(
                                (opt) => opt.value === formData.nationalityId
                              )}
                              onChange={(selectedOption) =>
                                handleChange({
                                  target: {
                                    name: "nationalityId",
                                    value: selectedOption?.value,
                                  },
                                })
                              }
                              placeholder={`-- ${t("select")} --`}
                              isClearable
                              classNamePrefix={
                                errors.nationalityId ? "is-invalid" : ""
                              }
                            />
                            {errors.nationalityId && (
                              <FormError errorMessage={errors.nationalityId} />
                            )}
                          </div>

                          <div className="col-md-4">
                            <label htmlFor="countryId" className="form-label">
                              {t("country")}
                            </label>
                            <Select
                              id="countryId"
                              name="countryId"
                              options={formatOptions(countries, null)}
                              value={formatOptions(countries, null).find(
                                (opt) => opt.value === formData.countryId
                              )}
                              onChange={(selectedOption) =>
                                handleChange({
                                  target: {
                                    name: "countryId",
                                    value: selectedOption?.value,
                                  },
                                })
                              }
                              placeholder={t("selectCountry")}
                              isClearable
                              classNamePrefix={
                                errors.countryId ? "is-invalid" : ""
                              }
                            />
                            {errors.countryId && (
                              <FormError errorMessage={errors.countryId} />
                            )}
                          </div>

                          <div className="col-md-4">
                            <label htmlFor="stateId" className="form-label">
                              {t("state")}
                            </label>
                            <Select
                              id="stateId"
                              name="stateId"
                              options={formatOptions(states, null)}
                              value={formatOptions(states, null).find(
                                (opt) => opt.value === formData.stateId
                              )}
                              onChange={(selectedOption) =>
                                handleChange({
                                  target: {
                                    name: "stateId",
                                    value: selectedOption?.value,
                                  },
                                })
                              }
                              placeholder={t("selectState")}
                              isClearable
                              noOptionsMessage={() => t("noStates")}
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  borderColor: errors.stateId
                                    ? "red"
                                    : base.borderColor,
                                  boxShadow: errors.stateId
                                    ? "0 0 0 1px red"
                                    : base.boxShadow,
                                  "&:hover": {
                                    borderColor: errors.stateId
                                      ? "red"
                                      : base.borderColor,
                                  },
                                }),
                              }}
                            />
                            {errors.stateId && (
                              <FormError errorMessage={errors.stateId} />
                            )}
                          </div>

                          <div className="col-md-4">
                            <label htmlFor="cityId" className="form-label">
                              {t("city")}
                            </label>
                            <Select
                              id="cityId"
                              name="cityId"
                              options={formatOptions(cities, "city")}
                              value={formatOptions(cities, "city").find(
                                (opt) => opt.value === formData.cityId
                              )}
                              onChange={(selectedOption) =>
                                handleChange({
                                  target: {
                                    name: "cityId",
                                    value: selectedOption?.value,
                                  },
                                })
                              }
                              placeholder={t("selectCity")}
                              isClearable
                            />
                            {errors.cityId && (
                              <FormError errorMessage={errors.cityId} />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* قسم عني */}
                      <div className="form-section">
                        <h3
                          className="section-header text-primary-custom fw-bold"
                          data-translate="about"
                        >
                          {t("about-me")}
                        </h3>
                        <div className="row g-4">
                          {/* Short Bio in Arabic */}
                          <div className="col-md-6">
                            <label htmlFor="shortBioAr" className="form-label">
                              {t("short_bio_ar")}
                            </label>
                            <textarea
                              className="form-control"
                              id="shortBioAr"
                              name="arabicShortBiography"
                              rows="3"
                              maxLength={maxLength}
                              value={formData.arabicShortBiography}
                              onChange={handleChange}
                              style={{
                                borderColor: getColorForTextArea(
                                  formData.arabicShortBiography?.length || 0
                                ),
                                borderWidth: "2px",
                                transition: "border-color 0.3s ease",
                              }}
                            />
                            <div
                              className="form-text"
                              style={{
                                borderColor: getColorForTextArea(
                                  formData.arabicShortBiography?.length || 0
                                ),
                              }}
                            >
                              {t("maximum_150_characters")} –{" "}
                              {maxLength -
                                (formData.arabicShortBiography?.length ||
                                  0)}{" "}
                              {t("characters_left")}
                            </div>
                          </div>

                          {/* Short Bio in English */}
                          <div className="col-md-6">
                            <label htmlFor="shortBioEn" className="form-label">
                              {t("short_bio_en")}
                            </label>
                            <textarea
                              className="form-control"
                              id="shortBioEn"
                              name="englishShortBiography"
                              rows="3"
                              maxLength={maxLength}
                              value={formData.englishShortBiography}
                              onChange={handleChange}
                              style={{
                                borderColor: getColorForTextArea(
                                  formData.arabicShortBiography?.length || 0
                                ),
                                borderWidth: "2px",
                                transition: "border-color 0.3s ease",
                              }}
                            />
                            <div
                              className="form-text"
                              style={{
                                color: getColorForTextArea(
                                  formData.englishShortBiography?.length || 0
                                ),
                              }}
                            >
                              {t("maximum_150_characters")} –{" "}
                              {maxLength -
                                (formData.englishShortBiography?.length ||
                                  0)}{" "}
                              {t("characters_left")}
                            </div>
                          </div>

                          {/* Full Bio in Arabic */}
                          <div className="col-12">
                            <label htmlFor="longBioAr" className="form-label">
                              {t("long_bio_ar")}
                            </label>
                            <textarea
                              className="form-control"
                              id="longBioAr"
                              name="arabicCompleteBiography"
                              rows="5"
                              maxLength={maxFullLength}
                              value={formData.arabicCompleteBiography}
                              onChange={handleChange}
                              style={{
                                borderColor: getColorForTextArea(
                                  formData.arabicCompleteBiography?.length || 0,
                                  maxFullLength
                                ),
                                borderWidth: "2px",
                                transition: "border-color 0.3s ease",
                              }}
                            />
                            <div
                              className="form-text"
                              style={{
                                color: getColorForTextArea(
                                  formData.arabicCompleteBiography?.length || 0,
                                  maxFullLength
                                ),
                              }}
                            >
                              {t("maximum_500_characters")} –{" "}
                              {maxFullLength -
                                (formData.arabicCompleteBiography?.length ||
                                  0)}{" "}
                              {t("characters_left")}
                            </div>
                          </div>

                          {/* Full Bio in English */}
                          <div className="col-12">
                            <label htmlFor="longBioEn" className="form-label">
                              {t("long_bio_en")}
                            </label>
                            <textarea
                              className="form-control"
                              id="longBioEn"
                              name="englishCompleteBiography"
                              rows="5"
                              maxLength={maxFullLength}
                              value={formData.englishCompleteBiography}
                              onChange={handleChange}
                              style={{
                                borderColor: getColorForTextArea(
                                  formData.arabicCompleteBiography?.length || 0,
                                  maxFullLength
                                ),
                                borderWidth: "2px",
                                transition: "border-color 0.3s ease",
                              }}
                            />
                            <div
                              className="form-text"
                              style={{
                                color: getColorForTextArea(
                                  formData.englishCompleteBiography?.length ||
                                    0,
                                  maxFullLength
                                ),
                              }}
                            >
                              {t("maximum_500_characters")} –{" "}
                              {maxFullLength -
                                (formData.englishCompleteBiography?.length ||
                                  0)}{" "}
                              {t("characters_left")}
                            </div>
                          </div>

                          {/* Industry */}
                          <div className="col-md-6">
                            <label htmlFor="industry" className="form-label">
                              {t("industry")}
                            </label>
                            <Select
                              id="industry"
                              name="industryId"
                              options={formatOptions(industries)}
                              value={formatOptions(industries, null).find(
                                (opt) => opt.value === formData.industryId
                              )}
                              onChange={(selectedOption) =>
                                setFormData({
                                  ...formData,
                                  industryId: selectedOption?.value,
                                  specialities: [],
                                })
                              }
                              placeholder={t("choose_industry")}
                              isClearable
                            />
                          </div>

                          {/* Specialties */}
                          <div className="col-md-6">
                            <label htmlFor="specialties" className="form-label">
                              {t("specialties")}
                            </label>
                            <Select
                              isMulti
                              id="specialties"
                              name="specialities"
                              options={formatOptions(specialities)}
                              value={formatOptions(specialities).filter((opt) =>
                                formData.specialities.includes(opt.value)
                              )}
                              onChange={(selectedOptions) => {
                                if (selectedOptions.length > 3) {
                                  toast.warn(
                                    t("select_up_to_three_specialties")
                                  );
                                  return;
                                }
                                setFormData({
                                  ...formData,
                                  specialities: selectedOptions.map(
                                    (opt) => opt.value
                                  ),
                                });
                              }}
                              placeholder={t("choose_up_to_three_specialties")}
                              isClearable
                              closeMenuOnSelect={false}
                              maxMenuHeight={200}
                            />
                            <div className="form-text">
                              {t("choose_up_to_three_specialties")}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* التكلفة  */}
                      {userRole === 3 && (
                        <div className="form-section">
                          <h3 className="section-header text-primary-custom fw-bold">
                            {t("is_participation_paid")}
                          </h3>
                          <div className="row g-4">
                            <div
                              className="experience-item card p-3 mb-3"
                              id="priceRangeContainer"
                            >
                              <div className="mb-3">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    id="paidYes"
                                    name="isPaid"
                                    value="true"
                                    checked={isPaid === true}
                                    onChange={() => setIsPaid(true)}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="paidYes"
                                  >
                                    {t("yes")}
                                  </label>
                                </div>

                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    id="paidNo"
                                    name="isPaid"
                                    value="false"
                                    checked={isPaid === false}
                                    onChange={() => setIsPaid(false)}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="paidNo"
                                  >
                                    {t("no")}
                                  </label>
                                </div>
                              </div>

                              {isPaid && (
                                <>
                                  <div className="mb-3">
                                    <label
                                      htmlFor="priceRange"
                                      className="form-label fw-bold"
                                    >
                                      {t("price_range")}
                                    </label>
                                    <select
                                      className="form-select"
                                      id="priceRange"
                                      name="priceRange"
                                      value={price}
                                      onChange={(e) => setPrice(e.target.value)}
                                    >
                                      <option value="">
                                        {t("select_price_range")}
                                      </option>
                                      {priceRanges.map((range) => {
                                        const value = `${range.minPrice}-${range.maxPrice}`;
                                        return (
                                          <option key={value} value={value}>
                                            {value}
                                          </option>
                                        );
                                      })}
                                    </select>

                                    <div className="form-text">
                                      {t("price_help")}
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <label className="form-label fw-bold">
                                      {t("show_price_in_profile")}
                                    </label>
                                    <div className="form-check form-switch">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="showPriceInProfile"
                                        checked={showPriceInProfile}
                                        onChange={(e) =>
                                          setShowPriceInProfile(
                                            e.target.checked
                                          )
                                        }
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor="showPriceInProfile"
                                      >
                                        {showPriceInProfile
                                          ? t("yes")
                                          : t("no")}
                                      </label>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* الخبرات والمؤهلات */}
                      {userRole === 3 && (
                        <div className="form-section">
                          <h3 className="section-header text-primary-custom fw-bold">
                            {t("experience_and_qualifications")}
                          </h3>

                          <div className="row mb-3">
                            <div className="col-12">
                              <p className="text-muted mb-3">
                                {t("add_your_experience_and_qualifications")}
                              </p>

                              <div id="experiencesContainer">
                                {formData.speakerExperiences.map(
                                  (experience, index) => (
                                    <div
                                      key={index}
                                      className="experience-item card p-3 mb-3"
                                    >
                                      <div className="row g-3">
                                        <div className="col-md-1 text-center d-flex align-items-start justify-content-center pt-3">
                                          <div className="experience-type-icon education-icon">
                                            <i className="fas fa-graduation-cap"></i>
                                          </div>
                                        </div>
                                        <div className="col-md-11">
                                          <div className="row g-3">
                                            <div className="col-md-6">
                                              <label className="form-label">
                                                {t("arabic_title")}
                                              </label>
                                              <input
                                                type="text"
                                                className="form-control"
                                                placeholder={t(
                                                  "example_arabic_title"
                                                )}
                                                value={experience.arabicTitle}
                                                onChange={(e) =>
                                                  handleChangeExperience(
                                                    index,
                                                    "arabicTitle",
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="col-md-6">
                                              <label className="form-label">
                                                {t("english_title")}
                                              </label>
                                              <input
                                                type="text"
                                                className="form-control"
                                                placeholder={t(
                                                  "example_english_title"
                                                )}
                                                value={experience.englishTitle}
                                                onChange={(e) =>
                                                  handleChangeExperience(
                                                    index,
                                                    "englishTitle",
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="col-md-6">
                                              <label className="form-label">
                                                {t("arabic_entity")}
                                              </label>
                                              <input
                                                type="text"
                                                className="form-control"
                                                placeholder={t(
                                                  "example_arabic_entity"
                                                )}
                                                value={experience.arabicEntity}
                                                onChange={(e) =>
                                                  handleChangeExperience(
                                                    index,
                                                    "arabicEntity",
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="col-md-6">
                                              <label className="form-label">
                                                {t("english_entity")}
                                              </label>
                                              <input
                                                type="text"
                                                className="form-control"
                                                placeholder={t(
                                                  "example_english_entity"
                                                )}
                                                value={experience.englishEntity}
                                                onChange={(e) =>
                                                  handleChangeExperience(
                                                    index,
                                                    "englishEntity",
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="col-md-6">
                                              <label className="form-label">
                                                {t("timing")}
                                              </label>
                                              <input
                                                type="text"
                                                className="form-control"
                                                placeholder={t(
                                                  "example_timing"
                                                )}
                                                value={experience.timing}
                                                onChange={(e) =>
                                                  handleChangeExperience(
                                                    index,
                                                    "timing",
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="col-md-6">
                                              <label className="form-label">
                                                {t("experience_type")}
                                              </label>
                                              <select
                                                className="form-select"
                                                value={
                                                  experience.experienceType
                                                }
                                                onChange={(e) =>
                                                  handleChangeExperience(
                                                    index,
                                                    "experienceType",
                                                    parseInt(e.target.value, 10)
                                                  )
                                                }
                                              >
                                                <option value="">
                                                  {t("select_experience_type")}
                                                </option>
                                                {experienceTypes.map((type) => (
                                                  <option
                                                    key={type.id}
                                                    value={type.id}
                                                  >
                                                    {type.name}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                            <div className="col-12 text-end">
                                              <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={() =>
                                                  removeExperience(index)
                                                }
                                              >
                                                <i className="fas fa-trash me-1"></i>{" "}
                                                {t("delete")}
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>

                              <div className="text-center mt-3">
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={addExperience}
                                >
                                  <i className="fas fa-plus me-1"></i>{" "}
                                  {t("add_new_experience")}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Job Information Section */}
                      <div className="form-section">
                        <h3
                          className="section-header text-primary-custom fw-bold"
                          data-translate="job_information"
                        >
                          {t("job_information")}
                        </h3>

                        <div className="row g-4">
                          <div className="col-md-6">
                            <label
                              htmlFor="organizationNameAr"
                              className="form-label"
                              data-translate="organization_name_ar"
                            >
                              {t("organization_name_ar")}
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="organizationNameAr"
                              name="organizationNameAr"
                              value={formData.arabicOrganizationName}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  arabicOrganizationName: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-md-6">
                            <label
                              htmlFor="organizationNameEn"
                              className="form-label"
                              data-translate="organization_name_en"
                            >
                              {t("organization_name_en")}
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="organizationNameEn"
                              name="organizationNameEn"
                              value={formData.englishOrganizationName}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  englishOrganizationName: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-md-6">
                            <label
                              htmlFor="jobTitleAr"
                              className="form-label"
                              data-translate="job_title_ar"
                            >
                              {t("job_title_ar")}
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="jobTitleAr"
                              name="jobTitleAr"
                              value={formData.arabicJobTitle}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  arabicJobTitle: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-md-6">
                            <label
                              htmlFor="jobTitleEn"
                              className="form-label"
                              data-translate="job_title_en"
                            >
                              {t("job_title_en")}
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="jobTitleEn"
                              name="jobTitleEn"
                              value={formData.englishJobTitle}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  englishJobTitle: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-12">
                            <label
                              htmlFor="organizationUrl"
                              className="form-label"
                              data-translate="organization_url"
                            >
                              {t("organization_url")}
                            </label>
                            <input
                              type="url"
                              className="form-control"
                              id="organizationUrl"
                              name="organizationUrl"
                              dir="ltr"
                              placeholder="https://..."
                              value={formData.organizationLink}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  organizationLink: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* Social Media Accounts Section */}
                      <div className="form-section">
                        <h3
                          className="section-header text-primary-custom fw-bold"
                          data-translate="social_media_accounts"
                        >
                          {t("social_media_accounts")}
                        </h3>
                        <div className="text-muted mb-4">
                          <small data-translate="optional">
                            {t("optional")}
                          </small>
                        </div>

                        <div className="row g-4">
                          {/* Google Scholar */}
                          <div className="col-md-6">
                            <label
                              htmlFor="googleScholar"
                              className="form-label"
                              data-translate="google_scholar"
                            >
                              {t("google_scholar")}
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">
                                <i className="fas fa-graduation-cap"></i>
                              </span>
                              <input
                                type="url"
                                className="form-control"
                                id="googleScholar"
                                name="googleScholar"
                                dir="ltr"
                                value={formData.scholarLink}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    scholarLink: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>

                          {/* Website */}
                          <div className="col-md-6">
                            <label
                              htmlFor="website"
                              className="form-label"
                              data-translate="website"
                            >
                              {t("website")}
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">
                                <i className="fas fa-globe"></i>
                              </span>
                              <input
                                type="url"
                                className="form-control"
                                id="website"
                                name="website"
                                dir="ltr"
                                value={formData.websiteLink}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    websiteLink: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>

                          {/* LinkedIn */}
                          <div className="col-md-6">
                            <label
                              htmlFor="linkedin"
                              className="form-label"
                              data-translate="linkedin"
                            >
                              {t("linkedin")}
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">
                                <i className="fab fa-linkedin"></i>
                              </span>
                              <input
                                type="url"
                                className="form-control"
                                id="linkedin"
                                name="linkedin"
                                dir="ltr"
                                value={formData.linkedInLink}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    linkedInLink: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>

                          {/* X (Twitter) */}
                          <div className="col-md-6">
                            <label
                              htmlFor="twitter"
                              className="form-label"
                              data-translate="twitter"
                            >
                              {t("twitter")}
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">
                                <i className="fab fa-x-twitter"></i>
                              </span>
                              <input
                                type="url"
                                className="form-control"
                                id="twitter"
                                name="twitter"
                                dir="ltr"
                                value={formData.xLink}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    xLink: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>

                          {/* YouTube */}
                          <div className="col-md-6">
                            <label
                              htmlFor="youtube"
                              className="form-label"
                              data-translate="youtube"
                            >
                              {t("youtube")}
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">
                                <i className="fab fa-youtube"></i>
                              </span>
                              <input
                                type="url"
                                className="form-control"
                                id="youtube"
                                name="youtube"
                                dir="ltr"
                                value={formData.youtubeLink}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    youtubeLink: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>

                          {/* Facebook */}
                          <div className="col-md-6">
                            <label
                              htmlFor="facebook"
                              className="form-label"
                              data-translate="facebook"
                            >
                              {t("facebook")}
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">
                                <i className="fab fa-facebook"></i>
                              </span>
                              <input
                                type="url"
                                className="form-control"
                                id="facebook"
                                name="facebook"
                                dir="ltr"
                                value={formData.facebookLink}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    facebookLink: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>

                          {/* GitHub */}
                          <div className="col-md-6">
                            <label
                              htmlFor="github"
                              className="form-label"
                              data-translate="github"
                            >
                              {t("github")}
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">
                                <i className="fab fa-github"></i>
                              </span>
                              <input
                                type="url"
                                className="form-control"
                                id="github"
                                name="github"
                                dir="ltr"
                                value={formData.githubLink}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    githubLink: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* upload CV Section */}
                      <div className="form-section">
                        <h3
                          className="section-header text-primary-custom fw-bold"
                          data-translate="social_media_accounts"
                        >
                          {t("uploadCV")}
                        </h3>
                        <div className="text-muted mb-4">
                          <small data-translate="optional">
                            {t("optional")}
                          </small>
                        </div>
                        <input
                          type="file"
                          id="cv"
                          name="cvLink"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                        />
                        {formData.file &&
                          formData.file.type === "application/pdf" && (
                            <iframe
                              src={URL.createObjectURL(formData.file)}
                              title="CV Preview"
                              className="mt-3"
                              style={{
                                width: "100%",
                                height: "400px",
                                border: "1px solid #ccc",
                              }}
                            />
                          )}

                        {formData.cvLink && (
                          <div className="mt-2">
                            <a
                              href={formData.cvLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-outline-success"
                            >
                              {t("viewCV")}
                            </a>
                          </div>
                        )}

                        {formData.file && (
                          <div className="mt-2 text-success">
                            <small>تم اختيار الملف: {formData.file.name}</small>
                          </div>
                        )}
                      </div>

                      {/* <!-- Form Actions --> */}
                      <div className="d-flex justify-content-end mt-5">
                        <a
                          href="/profile"
                          className="btn btn-outline-secondary me-3"
                          data-translate="cancel"
                        >
                          {t("cancel")}
                        </a>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          data-translate="save_changes"
                        >
                          {t("save_changes")}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ToastContainer />
      </div>
    </>
  );
};

export default ProfileEdit;
